/* global module */
/* eslint-disable complexity, no-await-in-loop, unicorn/consistent-function-scoping */
(function (root, factory) {
  const api = factory(root)

  if (typeof module === 'object' && module.exports) {
    module.exports = api
  }

  if (root) {
    root.DataRaporChartLoader = api
  }
})(typeof globalThis === 'undefined' ? this : globalThis, root => {
  const HOUR_MS = 60 * 60 * 1000
  const MAX_MINUTE_RANGE_MS = 31 * 24 * HOUR_MS
  const MINUTE_RANGE_MESSAGE = 'Dakikalık görünüm en fazla 31 günlük aralıkta kullanılabilir. Daha uzun aralıklar için saatlik görünümü seçebilirsiniz.'
  const DATE_ORDER_MESSAGE = 'Bitiş tarihi başlangıç tarihinden önce olamaz.'
  const DATE_REQUIRED_MESSAGE = 'Lütfen geçerli bir başlangıç ve bitiş tarihi seçin.'

  function toTimestamp(value) {
    if (value instanceof Date) {
      return value.getTime()
    }

    if (typeof value === 'number') {
      return value
    }

    if (typeof value !== 'string' || !value.trim()) {
      return Number.NaN
    }

    return new Date(value).getTime()
  }

  function createRequestPlan({ fromDate, toDate, frequency }) {
    const fromTimestamp = toTimestamp(fromDate)
    const toTimestampValue = toTimestamp(toDate)

    if (!Number.isFinite(fromTimestamp) || !Number.isFinite(toTimestampValue)) {
      return {
        valid: false,
        code: 'INVALID_DATE',
        message: DATE_REQUIRED_MESSAGE,
        frequencies: []
      }
    }

    const rangeMs = toTimestampValue - fromTimestamp
    if (rangeMs < 0) {
      return {
        valid: false,
        code: 'END_BEFORE_START',
        message: DATE_ORDER_MESSAGE,
        rangeMs,
        frequencies: []
      }
    }

    if (frequency === 'Minute') {
      if (rangeMs > MAX_MINUTE_RANGE_MS) {
        return {
          valid: true,
          code: 'SERVER_RANGE_VALIDATION',
          message: '',
          rangeMs,
          frequencies: ['Minute'],
          progressive: false,
          minuteSkipped: false
        }
      }

      return {
        valid: true,
        code: 'PROGRESSIVE_MINUTE',
        message: '',
        rangeMs,
        frequencies: ['Hour', 'Minute'],
        progressive: true,
        minuteSkipped: false
      }
    }

    return {
      valid: true,
      code: 'SINGLE_RESOLUTION',
      message: '',
      rangeMs,
      frequencies: [frequency],
      progressive: false,
      minuteSkipped: false
    }
  }

  function subtractExactDays(endValue, days = 31) {
    const endTimestamp = toTimestamp(endValue)
    if (!Number.isFinite(endTimestamp)) {
      return null
    }

    return new Date(endTimestamp - (days * 24 * HOUR_MS))
  }

  function isAbortError(error) {
    return Boolean(error && (error.name === 'AbortError' || error.code === 'ABORT_ERR'))
  }

  function normalizeHistoryRows(rows) {
    const seen = new Set()
    // NexBus already applies gap/source priority. Keep its first row for a
    // timestamp + tag; never resample, aggregate or select sources here.
    return (Array.isArray(rows) ? rows : []).filter(row => {
      const key = JSON.stringify([row.TagName, row.Date || row.TimeStamp])
      if (seen.has(key)) {
        return false
      }

      seen.add(key)
      return true
    }).map(row => ({ ...row, Value: Object.prototype.hasOwnProperty.call(row, 'Average') ? row.Average : row.Value }))
  }

  function isCoarseFallback(row) {
    const rank = { Second: 1, Minute: 2, Hour: 3 }
    return rank[row?.ActualResolution] > rank[row?.RequestedResolution]
  }

  function historyRowDescription(row) {
    if (!row) {
      return ''
    }

    const fields = []
    if (row.ActualResolution) {
      fields.push(`Gerçek: ${row.ActualResolution} (istenen: ${row.RequestedResolution || '—'})`)
    }

    if (row.Source) {
      fields.push(`Kaynak: ${row.Source}`)
    }

    if (row.FallbackReason) {
      const aggregate = /Aggregat|FromFiner/i.test(row.FallbackReason)
      fields.push(`${isCoarseFallback(row) ? 'Daha kaba geçmiş' : (aggregate ? 'İnce veriden özet' : 'Kaynak geçişi')}: ${row.FallbackReason}`)
    }

    for (const field of ['Minimum', 'Maximum', 'SampleCount', 'Quality', 'AnyTrue', 'AllTrue']) {
      if (row[field] !== undefined && row[field] !== null) {
        fields.push(`${field}: ${row[field]}`)
      }
    }

    return fields.join(' · ')
  }

  function historySummary(rows) {
    const coarse = rows.filter(isCoarseFallback).length
    const changed = rows.filter(row => row.ActualResolution && row.RequestedResolution && row.ActualResolution !== row.RequestedResolution).length
    const reasons = [...new Set(rows.map(row => row.FallbackReason).filter(Boolean))]
    if (!changed && !reasons.length) {
      return ''
    }

    return `${coarse} daha kaba geçmiş noktası; ${changed} noktada farklı çözünürlük. ${reasons.join(', ')}. Eksik aralıklar doldurulmaz; kaba noktalar birleştirilmez.`
  }

  class ChartDataRequestError extends Error {
    constructor(code, status) {
      super(code)
      this.name = 'ChartDataRequestError'
      this.code = code
      this.status = status || 0
    }
  }

  class ProgressiveChartLoader {
    constructor(options = {}) {
      this.fetchImpl = options.fetchImpl || (root && root.fetch ? root.fetch.bind(root) : null)
      this.AbortControllerImpl = options.AbortControllerImpl || (root && root.AbortController)
      this.endpoint = options.endpoint || '/api/v1/chart/multi-tags-data'
      this.cacheTtlMs = Number.isFinite(options.cacheTtlMs) ? options.cacheTtlMs : 30 * 1000
      this.maxCacheEntries = Number.isFinite(options.maxCacheEntries) ? options.maxCacheEntries : 12
      this.cache = new Map()
      this.generation = 0
      this.activeController = null
    }

    cancel() {
      this.generation += 1
      if (this.activeController) {
        this.activeController.abort()
        this.activeController = null
      }
    }

    isCurrent(token) {
      return Boolean(
        token &&
        token.generation === this.generation &&
        token.controller === this.activeController &&
        !token.controller.signal.aborted
      )
    }

    clearCache() {
      this.cache.clear()
    }

    _begin() {
      if (!this.fetchImpl || !this.AbortControllerImpl) {
        throw new Error('Fetch and AbortController are required')
      }

      if (this.activeController) {
        this.activeController.abort()
      }

      const controller = new this.AbortControllerImpl()
      const token = {
        generation: this.generation + 1,
        controller
      }
      this.generation = token.generation
      this.activeController = controller
      return token
    }

    _cacheKey(payload) {
      return JSON.stringify([
        payload.tagNameArray || '',
        payload.tagIdArray || '',
        payload.fromDate || '',
        payload.toDate || '',
        payload.freq || ''
      ])
    }

    _readCache(key) {
      const cached = this.cache.get(key)
      if (!cached) {
        return null
      }

      if (Date.now() - cached.createdAt > this.cacheTtlMs) {
        this.cache.delete(key)
        return null
      }

      return cached.result
    }

    _writeCache(key, result) {
      if (this.cache.has(key)) {
        this.cache.delete(key)
      }

      this.cache.set(key, { createdAt: Date.now(), result })
      while (this.cache.size > this.maxCacheEntries) {
        this.cache.delete(this.cache.keys().next().value)
      }
    }

    async _request(payload, signal) {
      const cacheKey = this._cacheKey(payload)
      const cached = this._readCache(cacheKey)
      if (cached) {
        return { result: cached, fromCache: true }
      }

      const response = await this.fetchImpl(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal
      })

      if (!response.ok) {
        throw new ChartDataRequestError('CHART_REQUEST_FAILED', response.status)
      }

      let result
      try {
        result = await response.json()
      } catch {
        throw new ChartDataRequestError('INVALID_CHART_RESPONSE', response.status)
      }

      if (!result || result.success === false || !Array.isArray(result.data)) {
        throw new ChartDataRequestError('INVALID_CHART_RESPONSE', response.status)
      }

      this._writeCache(cacheKey, result)
      return { result, fromCache: false }
    }

    async load(query, handlers = {}) {
      const token = this._begin()
      const plan = createRequestPlan({
        fromDate: query.validationFromDate || query.fromDate,
        toDate: query.validationToDate || query.toDate,
        frequency: query.frequency
      })
      const isCurrent = () => this.isCurrent(token)

      if (typeof handlers.onPlan === 'function') {
        await handlers.onPlan({ plan, isCurrent })
      }

      if (!plan.valid || !isCurrent()) {
        if (plan.valid === false && typeof handlers.onValidationError === 'function' && isCurrent()) {
          await handlers.onValidationError({ plan, isCurrent })
        }

        if (isCurrent()) {
          this.activeController = null
        }

        return { status: plan.valid ? 'stale' : 'invalid', plan }
      }

      for (let index = 0; index < plan.frequencies.length; index += 1) {
        if (!isCurrent()) {
          return { status: 'stale', plan }
        }

        const frequency = plan.frequencies[index]
        const stage = {
          frequency,
          index,
          isPreview: plan.progressive && frequency === 'Hour',
          isDetail: plan.progressive && frequency === 'Minute',
          hasNext: index < plan.frequencies.length - 1,
          plan,
          isCurrent
        }

        try {
          const payload = {
            tagNameArray: query.tagNameArray,
            tagIdArray: query.tagIdArray,
            fromDate: query.fromDate,
            toDate: query.toDate,
            freq: frequency
          }
          const response = await this._request(payload, token.controller.signal)
          if (!isCurrent()) {
            return { status: 'stale', plan }
          }

          if (typeof handlers.onData === 'function') {
            await handlers.onData({
              ...stage,
              data: response.result.data,
              result: response.result,
              fromCache: response.fromCache
            })
          }

          if (!isCurrent()) {
            return { status: 'stale', plan }
          }
        } catch (error) {
          if (isAbortError(error) || !isCurrent()) {
            return { status: 'aborted', plan }
          }

          if (typeof handlers.onError === 'function') {
            await handlers.onError({ ...stage, error })
          }

          if (!stage.hasNext) {
            if (isCurrent()) {
              this.activeController = null
            }

            return { status: 'error', plan, error }
          }
        }
      }

      if (isCurrent() && typeof handlers.onComplete === 'function') {
        await handlers.onComplete({ plan, isCurrent })
      }

      const completed = isCurrent()
      if (completed) {
        this.activeController = null
      }

      return { status: completed ? 'complete' : 'stale', plan }
    }
  }

  return {
    MAX_MINUTE_RANGE_MS,
    MINUTE_RANGE_MESSAGE,
    DATE_ORDER_MESSAGE,
    DATE_REQUIRED_MESSAGE,
    ChartDataRequestError,
    ProgressiveChartLoader,
    createRequestPlan,
    subtractExactDays,
    toTimestamp,
    normalizeHistoryRows,
    isCoarseFallback,
    historyRowDescription,
    historySummary
  }
})
